<i18n>
{
  "en": {
    "settings": {
      "title": "Settings",
      "daemon": "Notification service",
      "agent": "Service processes launching",
      "logСlearing": "Automatic log clearing",
      "defaultSubscribers": "Default addresses",
      "maintenance": "Maintenance",
      "maintenanceButton": "Assign tasks to agents to update model descriptions",
      "testSave": {
        "title": "Save and Test",
        "message": "Do you really wish to save default addresses and send test message?"
      },
      "subscribers": {
        "message": "The system has notifications (N pcs), to which only general addresses are subscribed. If you now keep the list of general recipient addresses empty, these notifications will not be sent to anyone. Would you like to continue?"
      }
    }
  },
  "ru": {
    "settings": {
      "title": "Настройки",
      "daemon": "Служба уведомлений",
      "agent": "Запуск сервисных процессов",
      "logСlearing": "Автоматическая очистка журнала",
      "defaultSubscribers": "Получатели по умолчанию",
      "maintenance": "Обслуживание",
      "maintenanceButton": "Поставить задачи агентам на обновление описаний моделей",
      "testSave": {
        "title": "Сохранить и проверить",
        "message": "Вы действительно хотите сохранить получателя по умолчанию и отправить тестовое сообщение?"
      },
      "subscribers": {
        "message": "В системе есть уведомления (N шт), на которые подписаны только общие адреса. Если сейчас сохранить пустой список общих адресов получателей, то эти уведомления не будут никому отправляться. Желаете продолжить?"
      }
    }
  }
}
</i18n>

<template>
  <div class="content-block dx-card responsive-paddings">
    <div class="container">
      <div class="card-settings">
        <dx-form
          v-if="Object.keys(settings).length > 0"
          id="form"
          @initialized=initializedForm
          label-location="left"
          :form-data="formData"
          :col-count="1"
          :show-colon-after-label="false"
          :align-item-labels="true"
          :disabled="saveDisabled"
          validation-group="settingsValidation"
          @field-data-changed="onFieldDataChanged"
        >
          <dx-group-item :col-count="1" :caption="$t('settings.daemon')">
            <dx-simple-item data-field="SMTP_HOST" :editor-options="{inputAttr: {autocomplete:'off'}}">
              <dx-label :text="settings['SMTP_HOST'].name" />

              <dx-required-rule :message="$t('general.mandatory')" />
              <dx-validation-rule type="custom" :validation-callback="hostOrIPValidation" :message="$t('general.badHost')" />
            </dx-simple-item>

            <dx-simple-item data-field="SMTP_PORT" editor-type="dxNumberBox">
              <dx-label :text="settings['SMTP_PORT'].name" />

              <dx-required-rule :message="$t('general.mandatory')" />
              <dx-validation-rule type="range" :min="1" :max="65535" :message="$t('general.badPort')" />
            </dx-simple-item>

            <dx-simple-item data-field="SMTP_USERNAME" :editor-options="{inputAttr: {autocomplete:'off'}}">
              <dx-label :text="settings['SMTP_USERNAME'].name" />
            </dx-simple-item>

            <dx-simple-item data-field="SMTP_PASSWORD" :editorOptions="{inputAttr: { autocomplete: 'off' }, mode: settings['SMTP_PASSWORD'].isWriteOnly ? 'password': null}">
              <dx-label :text="settings['SMTP_PASSWORD'].name" />
            </dx-simple-item>

            <dx-simple-item data-field="SMTP_SSL">
              <dx-label :text="settings['SMTP_SSL'].name" />
            </dx-simple-item>

            <dx-simple-item data-field="SMTP_TRUST-ALL" :visible="isSslEnabled">
              <dx-label :text="settings['SMTP_TRUST-ALL'].name" />
            </dx-simple-item>

            <dx-simple-item data-field="SMTP_START-TLS" editor-type="dxSelectBox"
              :editor-options="{
                dataSource: ['OPTIONAL', 'DISABLED', 'REQUIRED'],
              }"
            >
              <dx-label :text="settings['SMTP_START-TLS'].name" />
            </dx-simple-item>

            <dx-simple-item data-field="SMTP_FROM">
              <dx-label :text="settings['SMTP_FROM'].name" />

              <dx-validation-rule type="pattern" :pattern="validationPatterns.email" :message="$t('general.badEmail')" />
            </dx-simple-item>
          </dx-group-item>
        
          <dx-group-item :col-count="2">
            <dx-group-item :col-count="1" :caption="$t('settings.agent')">
              <dx-simple-item data-field="PROCESSES_PERIODICITY"
                editorType="dxSelectBox"
                :editor-options="{ dataSource: processesPeriodicity }"
              >
                <dx-label :text="settings['PROCESSES_PERIODICITY'].name" />
                <dx-required-rule :message="$t('general.mandatory')" />
              </dx-simple-item>
            </dx-group-item>

            <dx-group-item :col-count="1" :caption="$t('settings.logСlearing')">
              <dx-simple-item
                data-field="LOG_STORAGE_DAYS"
                :editor-options="{ min: 0, max: 3650 }"
              >
                <dx-label :text="settings['LOG_STORAGE_DAYS'].name" />
              </dx-simple-item>
              <dx-simple-item data-field="CLEARING_ENABLED">
                <dx-label :text="settings['CLEARING_ENABLED'].name" />
              </dx-simple-item>
            </dx-group-item>
          </dx-group-item>

          <dx-group-item v-if="isCloudMode && isDefaultAdmin"
            :col-count="1" :caption="$t('settings.maintenance')"
          >
              <dx-simple-item>
                <template #default>
                  <dx-button type="normal"
                    :text="$t('settings.maintenanceButton')"
                    styling-mode="contained"
                    :disabled="isSetTasksAgents"
                    @click="setTaskAgentOnServer" />
                </template>
              </dx-simple-item>
            </dx-group-item>
        </dx-form>
      </div>

      <div id="buttons" class="app-form-buttons">
        <aims2-button
          icon="save"
          type="default"
          :text="$t('general.save')"
          :hint="$t('device.detail.actions.saveDevice')"
          :busy="saving"
          :disabled="loading || saveDisabled"
          @click="onSave"
        />
      </div>
    </div>
  </div>
</template>

<script>
import baseView from "../base.vue";
import appInfo from "../../app-info";

import {
  DxForm, DxGroupItem, DxSimpleItem,
  DxLabel, DxRequiredRule, DxValidationRule
} from 'devextreme-vue/form';
import DxButton from "devextreme-vue/button";
import ValidationEngine from 'devextreme/ui/validation_engine';

import { notifier } from '@/shared/lib/notifier/index';

import aims2Button from '@/components/active-button.vue';

import { getSettingsAll, putSettings, notifyingAgents } from "@/services/api";
import { AclHandler } from "@/services/acl-handler";

import { validationPatterns } from "@/utils/validation-patterns";

export default {
  extends: baseView,
  components: {
    DxForm, DxGroupItem, DxSimpleItem, aims2Button,
    DxLabel, DxRequiredRule, DxValidationRule, DxButton
  },
  data() {
    return {
      validationPatterns,
      isCloudMode: this.$store.getters.isCloudMode,
      processesPeriodicity: [5, 10, 15, 20, 30, 60],
      settings: {},
      formData: {},
      fromInstance: null,
      loading: false,
      saving: false,
      isSetTasksAgents: false,
      saveDisabled: false,
      isSslEnabled: false,
    };
  },
  computed: {
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    isDefaultAdmin() {
      return this.aclUser.hasPermission({ roleCodes : ['ADMIN', 'DEVELOPER'] }) && this.aclUser.hasDefaultLocation();
    },
  },
  methods: {
    initializedForm(e) {
      this.fromInstance = e.component;
    },
    setTaskAgentOnServer() {
      this.isSetTasksAgents = true;

      notifyingAgents()
        .then(() => this.isSetTasksAgents = false)
        .catch((error) => {
          this.isSetTasksAgents = false;
          this.onDataError(error);
        });
    },
    hostOrIPValidation({ value }) {
      if (!value) return true;
      if (value.match(validationPatterns.ip)) return true;
      if (value.match(validationPatterns.hostname)) return true;
      return false;
    },
    async load() {
      try {
        this.loading = true;

        let settings = await getSettingsAll();
        settings = settings.filter(setting => setting.systemComponent !== 'SYSTEM');

        this.settings = {};
        const formData = {};
        settings.forEach(rec => {
          switch (rec.valueType) {
            case "S":
              formData[rec.code] = rec.stringValue;
              break;
            case "N":
              formData[rec.code] = rec.numericValue;
              break;
            case "D":
              formData[rec.code] = rec.dateValue;
              break;
            case "F":
              formData[rec.code] = rec.flagValue;
              break;
          }

          this.settings[rec.code] = rec;
        });

        this.formData = formData;
      } catch(err) {
        this.onDataError(err);
      } finally {
        this.loading = false;
      }
    },
    async onFieldDataChanged({ dataField, value }) {
      switch (this.settings[dataField] && this.settings[dataField].valueType) {
        case "S":
          this.settings[dataField].stringValue = value;
          break;
        case "N":
          this.settings[dataField].numericValue = value;
          break;
        case "D":
          this.settings[dataField].dateValue = value;
          break;
        case "F":
          this.settings[dataField].flagValue = value;
          break;
        default:
          break;
      }

      if (dataField === 'SMTP_SSL') {
        this.isSslEnabled = !!this.formData.SMTP_SSL;
        if (!this.formData.SMTP_SSL) {
          this.fromInstance.updateData('SMTP_PORT', 25);
          this.fromInstance.updateData('SMTP_TRUST-ALL', false);
        } else {
          this.fromInstance.updateData('SMTP_PORT', this.formData['SMTP_START-TLS'] === 'REQUIRED'? 587: 465);
        }
      }
      if (dataField === 'SMTP_START-TLS' && this.formData.SMTP_SSL) {
        this.fromInstance.updateData('SMTP_PORT', this.formData['SMTP_START-TLS'] === 'REQUIRED'? 587: 465);
      }
    },
    validate() {
      return ValidationEngine.validateGroup("settingsValidation");
    },
    async onSave() {
      const validate = this.validate();
      if (!validate.isValid) {
        validate.brokenRules[0].validator.focus();  
        return;
      }

      try {
        this.saving = true;
        this.saveDisabled = true;

        const save = Object.values(this.settings);
        await putSettings(save);

        notifier.showSuccess(this.$t('general.updateSuccess'));
      } catch (error) {
        this.onDataError(error);
      } finally {
        this.saving = false;
        this.saveDisabled = false;
      }
    },
  },
  created() {
    document.title = `${appInfo.title} - ${this.$t('settings.title')}`;
    this.$store.commit("setBreadcrumb", [{
      link: "/settings",
      title: `${this.$t('settings.title')}`
    }]);

    this.load();
  },
  watch: {
  }
}
</script>

<style lang="scss" scoped>
.container {
    max-width: 800px;
}

.app-form-buttons {
  .dx-button {
    width: 200px;
  }
}
</style>
